import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowRight } from "lucide-react";

const ROTATING = ["zur PDL.", "zur Praxisanleitung.", "zur Fachassistenz.", "zum Wundexperten.", "zur Betreuungskraft."];

const heroLernen = "/images/hero-skandi-lernen.png";
const heroBook = "/images/hero-skandi-book.png";
const heroGespraech = "/images/hero-skandi-gespraech.png";

/**
 * /design-preview · Cinematic-Studie ("Reel-Look für die Homepage")
 * Eigenständige Testseite — nirgendwo verlinkt.
 */

const C = {
  cream: "#F7F2EB",
  cream2: "#EFE8DD",
  ink: "#3A342E",
  soft: "#7A6F63",
  terra: "#A9542F",
  saffron: "#E2A33C",
  champ: "#E7CFA2",
  hairline: "rgba(58,52,46,0.14)",
};

const serif = { fontFamily: "'Playfair Display', Georgia, serif" };
const sans = { fontFamily: "'Plus Jakarta Sans', sans-serif" };

const kicker = {
  ...sans,
  fontSize: 11,
  letterSpacing: "0.32em",
  textTransform: "uppercase",
  color: C.terra,
  fontWeight: 600,
};

/** Reel-Style · dunkler Editorial-Break mit Ken-Burns-Zoom */
function EditorialBreak() {
  return (
    <section data-testid="preview-editorial-break" style={{ position: "relative", minHeight: "88vh", display: "flex", alignItems: "center", justifyContent: "center", overflow: "hidden", textAlign: "center" }}>
      <img
        src="/images/cinema/break-wide.jpg"
        alt=""
        style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", animation: "dpKenburnsOut 22s ease-in-out infinite alternate" }}
      />
      <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(24,21,18,0.35) 0%, rgba(24,21,18,0.25) 40%, rgba(24,21,18,0.82) 100%)" }} />
      <div style={{ position: "relative", padding: "110px 32px", maxWidth: 900 }}>
        <div style={{ ...sans, fontSize: 11.5, letterSpacing: "0.34em", textTransform: "uppercase", color: C.champ, fontWeight: 700 }}>
          Online-live per Zoom · Keine Warteliste
        </div>
        <div style={{ width: 110, height: 1, background: C.saffron, margin: "26px auto 0" }} />
        <h2 style={{ ...serif, fontSize: "clamp(3rem, 7vw, 5.6rem)", lineHeight: 1.06, color: "#FAF8F4", fontWeight: 800, margin: "30px 0 0" }}>
          Start<br />
          <em style={{ fontStyle: "italic", color: C.champ, fontWeight: 700 }}>jeden Montag.</em>
        </h2>
        <p style={{ ...sans, fontSize: 16.5, lineHeight: 1.8, color: "rgba(240,235,226,0.9)", maxWidth: 520, margin: "34px auto 0" }}>
          Externenprüfung Pflegefachassistenz — 300 UE Vorbereitung,
          Prüfungsplätze über unsere Partnerschulen in NRW.
        </p>
        <div style={{ marginTop: 44, display: "flex", gap: 24, justifyContent: "center", flexWrap: "wrap", alignItems: "center" }}>
          <button data-testid="preview-break-cta" style={{ ...sans, background: "transparent", color: C.champ, border: `1.5px solid ${C.champ}`, borderRadius: 999, padding: "16px 36px", fontSize: 12.5, letterSpacing: "0.14em", textTransform: "uppercase", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}>
            Externenprüfung entdecken <ArrowRight size={15} />
          </button>
          <span style={{ ...serif, fontStyle: "italic", fontSize: 16, color: "rgba(240,235,226,0.85)" }}>
            2.490 € · Ratenzahlung möglich
          </span>
        </div>
      </div>
    </section>
  );
}

export default function DesignPreview() {
  const [idx, setIdx] = useState(0);
  useEffect(() => {
    const t = setInterval(() => setIdx((i) => (i + 1) % ROTATING.length), 2800);
    return () => clearInterval(t);
  }, []);
  useEffect(() => {
    const l = document.createElement("link");
    l.rel = "stylesheet";
    l.href = "https://fonts.googleapis.com/css2?family=Playfair+Display:ital,wght@0,400;0,500;0,600;0,800;1,400;1,500;1,700&display=swap";
    document.head.appendChild(l);
    return () => l.remove();
  }, []);

  return (
    <div style={{ background: C.cream, color: C.ink, minHeight: "100vh" }} data-testid="design-preview-page">
      <style>{`
        @keyframes dpFade { from { opacity: 0; transform: translateY(8px); } to { opacity: 1; transform: none; } }
        @keyframes dpKenburns { from { transform: scale(1); } to { transform: scale(1.09); } }
        @keyframes dpKenburnsOut { from { transform: scale(1.09); } to { transform: scale(1); } }
        @keyframes dpRise { from { opacity: 0; transform: translateY(22px); } to { opacity: 1; transform: none; } }
      `}</style>

      {/* Hinweis-Band */}
      <div style={{ ...sans, background: C.ink, color: C.cream, fontSize: 11, letterSpacing: "0.2em", textTransform: "uppercase", textAlign: "center", padding: "8px 16px", position: "relative", zIndex: 20 }}>
        Design-Studie · Cinematic / Reel-Look · nicht öffentlich verlinkt — <Link to="/" style={{ color: "#E9C9A8", textDecoration: "underline" }}>zurück zur echten Seite</Link>
      </div>

      {/* ============ CINEMATIC HERO · Full-Bleed wie im Reel ============ */}
      <section data-testid="preview-cinematic-hero" style={{ position: "relative", minHeight: "94vh", display: "flex", flexDirection: "column", overflow: "hidden" }}>
        <img
          src="/images/cinema/hero-wide.jpg"
          alt=""
          style={{ position: "absolute", inset: 0, width: "100%", height: "100%", objectFit: "cover", objectPosition: "center 55%", animation: "dpKenburns 24s ease-in-out infinite alternate" }}
        />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to bottom, rgba(24,21,18,0.42) 0%, rgba(24,21,18,0.14) 34%, rgba(24,21,18,0.38) 62%, rgba(24,21,18,0.9) 100%)" }} />
        <div style={{ position: "absolute", inset: 0, background: "linear-gradient(to right, rgba(24,21,18,0.5) 0%, rgba(24,21,18,0.22) 45%, rgba(24,21,18,0) 75%)" }} />

        {/* Transparenter Header auf dem Bild */}
        <header style={{ position: "relative", zIndex: 10 }}>
          <div style={{ maxWidth: 1240, margin: "0 auto", padding: "26px 32px", display: "flex", alignItems: "center", justifyContent: "space-between" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 16 }}>
              <img src="/dh_logo_transparent.webp" alt="Dickies-Campus" style={{ height: 44, width: "auto", filter: "brightness(0) invert(1)" }} />
              <span style={{ width: 1, height: 26, background: "rgba(247,242,235,0.35)" }} />
              <div>
                <div style={{ ...sans, fontSize: 13, letterSpacing: "0.28em", textTransform: "uppercase", fontWeight: 700, color: "#FAF8F4" }}>Dickies-Campus</div>
                <div style={{ ...sans, fontSize: 9, letterSpacing: "0.3em", textTransform: "uppercase", color: C.champ, marginTop: 2 }}>Akademie für Pflege</div>
              </div>
            </div>
            <nav style={{ ...sans, display: "flex", gap: 34, fontSize: 13, color: "rgba(247,242,235,0.85)" }} className="hidden md:flex">
              {["Weiterbildungen", "Pflege-KI", "Bibliothek", "Über uns"].map((n) => (
                <span key={n} style={{ cursor: "pointer" }}>{n}</span>
              ))}
            </nav>
            <button style={{ ...sans, background: "transparent", border: `1px solid ${C.champ}`, color: C.champ, borderRadius: 999, padding: "10px 24px", fontSize: 12, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 600, cursor: "pointer" }}>
              Beratung
            </button>
          </div>
        </header>

        {/* Hero-Text unten links */}
        <div style={{ position: "relative", zIndex: 10, marginTop: "auto", width: "100%" }}>
          <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 32px 84px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 14, animation: "dpRise 0.9s ease both" }}>
              <span style={{ ...sans, fontSize: 11, letterSpacing: "0.34em", textTransform: "uppercase", color: C.champ, fontWeight: 700 }}>
                Akademie für Pflege · Wissen, das weiterhilft
              </span>
              <span style={{ width: 54, height: 1, background: C.saffron }} />
            </div>
            <h1 style={{ ...serif, fontSize: "clamp(2.7rem, 6vw, 5rem)", lineHeight: 1.08, color: "#FAF8F4", fontWeight: 800, margin: "28px 0 0", animation: "dpRise 0.9s 0.15s ease both" }}>
              Du machst den Job.<br />
              Wir machen dich<br />
              <em key={idx} data-testid="preview-hero-rotating-word" style={{ color: C.champ, fontStyle: "italic", fontWeight: 700, display: "inline-block", animation: "dpFade 0.6s ease" }}>
                {ROTATING[idx]}
              </em>
            </h1>
            <p style={{ ...sans, fontSize: 16.5, lineHeight: 1.8, color: "rgba(240,235,226,0.88)", maxWidth: 460, marginTop: 30, animation: "dpRise 0.9s 0.3s ease both" }}>
              LG1/LG2, Praxisanleitung, PDL und Pflichtfortbildungen —
              AZAV-förderfähig, 100 % online live, ohne monatelange Wartezeiten.
            </p>
            <div style={{ display: "flex", alignItems: "center", gap: 28, marginTop: 40, flexWrap: "wrap", animation: "dpRise 0.9s 0.45s ease both" }}>
              <button data-testid="preview-hero-cta" style={{ ...sans, background: C.terra, color: C.cream, border: "none", borderRadius: 999, padding: "17px 36px", fontSize: 13, letterSpacing: "0.12em", textTransform: "uppercase", fontWeight: 700, cursor: "pointer", display: "inline-flex", alignItems: "center", gap: 10 }}>
                Kostenlose Beratung <ArrowRight size={15} />
              </button>
              <span style={{ ...serif, fontStyle: "italic", fontSize: 16, color: "rgba(240,235,226,0.9)", borderBottom: "1px solid rgba(240,235,226,0.4)", paddingBottom: 2, cursor: "pointer" }}>
                oder erst in Ruhe die Kurse ansehen
              </span>
            </div>
            <div style={{ ...sans, display: "flex", gap: 18, marginTop: 54, fontSize: 11, letterSpacing: "0.22em", textTransform: "uppercase", color: "rgba(231,207,162,0.85)", fontWeight: 600, flexWrap: "wrap", animation: "dpRise 0.9s 0.6s ease both" }}>
              <span>AZAV-zertifiziert</span><span style={{ color: C.saffron }}>·</span>
              <span>Start jeden Montag</span><span style={{ color: C.saffron }}>·</span>
              <span>Bildungsgutschein</span><span style={{ color: C.saffron }}>·</span>
              <span>Ratenzahlung ohne Aufschlag</span>
            </div>
          </div>
        </div>
      </section>

      {/* Kurse · Cream bleibt als ruhiger Kontrast */}
      <section style={{ background: C.cream, padding: "100px 0 110px" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ display: "flex", alignItems: "baseline", justifyContent: "space-between", borderBottom: `1px solid ${C.hairline}`, paddingBottom: 26, marginBottom: 56 }}>
            <div>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={kicker}>Weiterbildungen</span>
                <span style={{ width: 44, height: 1, background: C.saffron }} />
              </div>
              <h2 style={{ ...serif, fontSize: "clamp(2rem, 3.4vw, 2.9rem)", fontWeight: 500, marginTop: 16 }}>
                Aus deinem ersten Kurs wird eine Karriere.
              </h2>
            </div>
            <span style={{ ...serif, fontStyle: "italic", fontSize: 15, color: C.soft, whiteSpace: "nowrap" }} className="hidden md:block">
              Alle 19 Kurse ansehen →
            </span>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "repeat(3, 1fr)", gap: 44 }} className="max-md:!grid-cols-1">
            {[
              { img: heroLernen, k: "Einstieg · 4 Wochen", t: "Ambulante Pflegeassistenz", p: "ab 87 €/Monat" },
              { img: heroBook, k: "Aufstieg · 84 UE", t: "Wundexperte", p: "ab 107 €/Monat" },
              { img: heroGespraech, k: "Führung · 720 UE", t: "Pflegedienstleitung", p: "Bildungsgutschein" },
            ].map((c) => (
              <div key={c.t} style={{ cursor: "pointer" }}>
                <div style={{ overflow: "hidden", borderRadius: 2 }}>
                  <img src={c.img} alt="" style={{ width: "100%", aspectRatio: "1.15", objectFit: "cover", transition: "transform 1.2s ease" }} onMouseEnter={(e) => { e.currentTarget.style.transform = "scale(1.05)"; }} onMouseLeave={(e) => { e.currentTarget.style.transform = "scale(1)"; }} />
                </div>
                <div style={{ ...sans, ...kicker, fontSize: 10.5, marginTop: 22 }}>{c.k}</div>
                <h3 style={{ ...serif, fontSize: 25, fontWeight: 500, marginTop: 10 }}>{c.t}</h3>
                <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", borderTop: `1px solid ${C.hairline}`, marginTop: 16, paddingTop: 14 }}>
                  <span style={{ ...serif, fontStyle: "italic", fontSize: 15, color: C.soft }}>{c.p}</span>
                  <ArrowRight size={16} color={C.terra} />
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============ EDITORIAL BREAK · Reel-Look ============ */}
      <EditorialBreak />

      {/* Pflege-KI · Terrakotta-Band */}
      <section style={{ background: C.terra, color: C.cream, padding: "100px 0" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 32px", display: "grid", gridTemplateColumns: "1fr 1fr", gap: 70, alignItems: "center" }} className="max-lg:!grid-cols-1">
          <div>
            <div style={{ ...kicker, color: C.saffron }}>Pflege-KI Assistenz</div>
            <h2 style={{ ...serif, fontSize: "clamp(2.1rem, 3.6vw, 3.1rem)", fontWeight: 500, lineHeight: 1.18, marginTop: 20 }}>
              Die Dokumentation schreibt sich jetzt <em style={{ fontStyle: "italic" }}>fast von allein.</em>
            </h2>
            <p style={{ ...sans, fontSize: 15.5, lineHeight: 1.85, color: "rgba(247,242,235,0.85)", maxWidth: 460, marginTop: 24 }}>
              Diktieren, strukturieren, freigeben — am Ende steht ein MDK-fertiges
              PDF. Eine digitale Patientenmappe für deinen ganzen Pflegedienst.
            </p>
          </div>
          <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 20 }} className="max-sm:!grid-cols-1">
            {[
              { n: "Kompakt", p: "199 €", d: "bis 10 Mitarbeiter" },
              { n: "Team", p: "349 €", d: "unbegrenztes Team" },
            ].map((pk) => (
              <div key={pk.n} style={{ background: C.cream, color: C.ink, padding: "34px 30px", borderRadius: 2 }}>
                <div style={{ ...sans, fontSize: 10.5, letterSpacing: "0.28em", textTransform: "uppercase", color: C.terra, fontWeight: 700 }}>{pk.d}</div>
                <div style={{ ...serif, fontSize: 24, fontWeight: 500, marginTop: 12 }}>Pflege-KI {pk.n}</div>
                <div style={{ ...serif, fontSize: 40, marginTop: 18 }}>{pk.p}<span style={{ ...sans, fontSize: 12, color: C.soft }}> /Monat</span></div>
                <div style={{ ...serif, fontStyle: "italic", fontSize: 14, color: C.soft, borderTop: `1px solid ${C.hairline}`, marginTop: 18, paddingTop: 14 }}>
                  Alle Funktionen. Monatlich kündbar.
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Bibliothek · Vogue-Spread */}
      <section style={{ background: C.cream, padding: "100px 0" }}>
        <div style={{ maxWidth: 1240, margin: "0 auto", padding: "0 32px" }}>
          <div style={{ display: "grid", gridTemplateColumns: "0.9fr 1.1fr", gap: 70, alignItems: "start" }} className="max-lg:!grid-cols-1">
            <div style={{ position: "sticky", top: 40 }}>
              <div style={{ display: "flex", alignItems: "center", gap: 14 }}>
                <span style={{ ...kicker }}>Die Bibliothek</span>
                <span style={{ width: 44, height: 1, background: C.saffron }} />
              </div>
              <h2 style={{ ...serif, fontSize: "clamp(2.2rem, 4vw, 3.4rem)", fontWeight: 500, lineHeight: 1.15, marginTop: 22 }}>
                Die Redaktion<br /><em style={{ fontStyle: "italic", color: C.terra }}>der Praxis.</em>
              </h2>
              <p style={{ ...sans, fontSize: 15, lineHeight: 1.85, color: C.soft, maxWidth: 380, marginTop: 24 }}>
                Zwölf Magazine aus dem Studio DH — Recht, Prüfung, Abrechnung,
                Menschen. Geschrieben wie ein guter Artikel, nicht wie ein Gesetzestext.
              </p>
              <p style={{ ...serif, fontStyle: "italic", fontSize: 19, color: C.saffron, marginTop: 30, lineHeight: 1.6, borderLeft: `2px solid ${C.saffron}`, paddingLeft: 20 }}>
                „Wissen, das aussieht wie ein Magazin — und wirkt wie eine Waffe im MD-Termin."
              </p>
            </div>
            <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr 1fr", gap: 26, alignItems: "end" }} className="max-sm:!grid-cols-2">
              {[
                "mdk-survival-guide", "wer-darf-was-atlas-vollversion", "abrechnungs-atlas-vollversion",
                "pflegegrad-spielbuch", "erschoepfungs-dienstplan", "kuerzungen-widerspruch-guide",
              ].map((s, i) => (
                <div key={s} style={{ transform: i % 2 ? "translateY(22px)" : "none" }}>
                  <img
                    src={`/downloads/thumbs/${s}.jpg`}
                    alt=""
                    style={{ width: "100%", boxShadow: "0 22px 45px -18px rgba(58,52,46,0.45)", borderRadius: 1 }}
                  />
                  <div style={{ ...sans, fontSize: 10, letterSpacing: "0.24em", textTransform: "uppercase", color: C.soft, marginTop: 12, textAlign: "center" }}>
                    Nº {String(i + 1).padStart(2, "0")} · <span style={{ color: C.saffron, fontWeight: 700 }}>Digital Edition</span>
                  </div>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      {/* Zitat + Footer */}
      <section style={{ padding: "110px 32px 80px", textAlign: "center" }}>
        <img src="/dh_logo_terra.webp" alt="Dickies-Campus" style={{ height: 54, width: "auto", margin: "0 auto", display: "block" }} />
        <p style={{ ...serif, fontStyle: "italic", fontSize: "clamp(1.5rem, 2.6vw, 2.1rem)", maxWidth: 720, margin: "26px auto 0", lineHeight: 1.5 }}>
          „Pflege trägt Menschen durchs Leben. Wir tragen die, die pflegen."
        </p>
        <div style={{ ...sans, ...kicker, marginTop: 30 }}>Wissen, das weiterhilft.</div>
        <div style={{ ...sans, fontSize: 12, color: C.soft, marginTop: 70, borderTop: `1px solid ${C.hairline}`, paddingTop: 26, maxWidth: 1136, margin: "70px auto 0", display: "flex", justifyContent: "space-between" }}>
          <span>Dickies-Campus GmbH · Düsseldorf</span>
          <span>www.dickies-helden.de</span>
        </div>
      </section>
    </div>
  );
}
